import React, { useRef, useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  Link
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmUserAccessAccount, resendTokenConfirmation } from './token-confirmation.api';
import { ga4 } from '../../utils/analytics/ga4';

const TokenConfirmation: React.FC = () => {
  const [values, setValues] = useState<string[]>(['', '', '', '', '', '']);
  const [apiErrors, setApiErrors] = useState<string[]>([]);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [resendLoading, setResendLoading] = useState<boolean>(false);
  const [resendMessage, setResendMessage] = useState<string>('');
  const [showResend, setShowResend] = useState<boolean>(false);
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userAccessGuid = searchParams.get('guid') || '';

  const handleResend = async () => {
    setApiErrors([]);
    setResendMessage('');
    setShowResend(false);
    if (!userAccessGuid) {
      setApiErrors(['GuId de acesso do usuário não encontrado na URL.']);
      return;
    }
    try {
      setResendLoading(true);
      const res = await resendTokenConfirmation({ userAccessGuid });
      if (res?.success && res?.data === true) {
        setResendMessage('Código reenviado! Verifique seu e-mail.');
        setShowResend(true);
        setTimeout(() => {
          setShowResend(false);
          setResendMessage('');
        }, 5000);
      } else {
        setApiErrors([res?.message || 'Não foi possível reenviar o token.']);
      }
    } catch (error) {
      const respData = (error as any)?.response?.data;
      const collected: string[] = [];
      if (respData) {
        if (Array.isArray(respData.errors)) {
          for (const item of respData.errors) {
            const msg = (item && (item.errorMessage || item.message)) as string | undefined;
            if (msg) collected.push(msg);
          }
        }
        if (respData.message && typeof respData.message === 'string') {
          collected.push(respData.message);
        }
      }
      setApiErrors(collected.length ? collected : ['Erro ao reenviar o token.']);
      console.error('Error resending confirmation token:', error);
    } finally {
      setResendLoading(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    const sanitized = value.slice(0, 1);
    const newValues = [...values];
    newValues[index] = sanitized;
    setValues(newValues);

    if (sanitized && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').trim().replace(/\s+/g, '');
    if (!text) {
      return;
    }
    e.preventDefault();
    const chars = text.slice(0, values.length).split('');
    const filled = [...values];
    for (let i = 0; i < chars.length; i++) {
      filled[i] = chars[i];
    }
    setValues(filled);
    const nextIndex = Math.min(chars.length, inputsRef.current.length - 1);
    inputsRef.current[nextIndex]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newValues = [...values];
      if (newValues[index]) {
        newValues[index] = '';
        setValues(newValues);
        inputsRef.current[Math.max(index - 1, 0)]?.focus();
        return;
      }
      if (index > 0) {
        newValues[index - 1] = '';
        setValues(newValues);
        inputsRef.current[index - 1]?.focus();
      }
      return;
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < inputsRef.current.length - 1) {
      inputsRef.current[index + 1]?.focus();
    }
  };

  const handleSubmit = async () => {
    setApiErrors([]);
    const token = values.join('');

    if (!userAccessGuid) {
      setApiErrors(['GuId de acesso do usuário não encontrado na URL.']);
      return;
    }
    if (!token || token.length !== values.length) {
      setApiErrors(['Por favor, preencha o código de 6 dígitos corretamente.']);
      return;
    }
    try {
      const res = await confirmUserAccessAccount({ userAccessGuid, token });
      if (res?.success) {
        ga4.event('token_confirmed_success');
        const msg = 'Sua conta foi ativada com sucesso. Você será redirecionado para o login...';
        setSuccessMessage(msg);
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
          setSuccessMessage('');
          navigate(`/planos?guid=${encodeURIComponent(userAccessGuid)}`);
        }, 3000);
      } else {
        ga4.event('token_confirmed_failed');
        setApiErrors([res?.message || 'Falha na confirmação do token.']);
      }
    } catch (error) {
      const respData = (error as any)?.response?.data;
      const collected: string[] = [];
      if (respData) {
        if (Array.isArray(respData.errors)) {
          for (const item of respData.errors) {
            const msg = (item && (item.errorMessage || item.message)) as string | undefined;
            if (msg) collected.push(msg);
          }
        }
        if (respData.message && typeof respData.message === 'string') {
          collected.push(respData.message);
        }
      }
      setApiErrors(collected.length ? collected : ['Erro ao confirmar o token.']);
      ga4.event('token_confirmed_failed');
      console.error('Error confirming account token:', error);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography variant="h5" component="h1" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
            Confirmação de Token
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Acabamos de enviar um código de confirmação para o seu e-mail.
          </Typography>
          <Typography variant="body2" sx={{ mb: 3 }}>
            Não recebeu o código?
            {' '}<Link component="button" onClick={handleResend} disabled={resendLoading} sx={{ ml: 0.5 }}>
              Reenviar token
            </Link>
          </Typography>

          {showSuccess && successMessage && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="success">{successMessage}</Alert>
            </Box>
          )}
          {showResend && resendMessage && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="success">{resendMessage}</Alert>
            </Box>
          )}

          {apiErrors.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {apiErrors.map((msg, idx) => (
                <Alert key={idx} severity="error" sx={{ mb: 1 }}>{msg}</Alert>
              ))}
            </Box>
          )}

          <Grid container spacing={2} justifyContent="center" sx={{ mb: 3, flexWrap: 'nowrap', overflowX: 'auto' }}>
            {values.map((val, idx) => (
              <Grid  component={'div' as React.ElementType} key={idx} item xs="auto">
                <TextField
                  inputRef={(el) => (inputsRef.current[idx] = el)}
                  value={val}
                  onChange={(e) => handleChange(idx, e.target.value)}
                  onPaste={idx === 0 ? handlePaste : undefined}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(idx, e)}
                  size="small"
                  sx={{ width: '3rem' }}
                  inputProps={{ maxLength: 1, style: { textAlign: 'center' } }}
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={() => navigate(-1)}>Voltar</Button>
            <Button variant="contained" onClick={handleSubmit} disabled={showSuccess}>Confirmar</Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default TokenConfirmation;
